import { ScanResult, MockViolation } from '../types/mockdetector.types';
import { DatabaseScanResult } from '../types/database.types';
import { generateAutoFix, AutoFixSuggestion } from './autoFix';
import { WIDGET_VERSION } from './version';
import { getConfidenceLevel } from './confidenceScoring';

/**
 * Calculate severity score for prioritization
 */
export const getSeverityScore = (severity: string): number => {
  switch (severity) {
    case 'critical': return 4;
    case 'high': return 3;
    case 'medium': return 2;
    case 'low': return 1;
    default: return 0;
  }
};

/**
 * Calculate priority score (severity × confidence)
 */
export const getPriorityScore = (violation: MockViolation): number => {
  const severityWeight = getSeverityScore(violation.severity);
  const confidenceWeight = (violation.confidence || 50) / 100;
  return severityWeight * confidenceWeight;
};

/**
 * Get business impact explanation based on violation
 */
const getBusinessImpact = (violation: MockViolation): string => {
  const desc = violation.description.toLowerCase();
  
  if (desc.includes('credential') || desc.includes('api key') || desc.includes('token')) {
    return 'CRITICAL SECURITY RISK: Exposed credentials can lead to data breaches and unauthorized access';
  }
  if (desc.includes('mock api') || desc.includes('test endpoint')) {
    return 'Development endpoint in production - API calls will fail for real users';
  }
  if (desc.includes('test email') || desc.includes('demo email')) {
    return 'Users won\'t receive notifications, password resets, or order confirmations';
  }
  if (desc.includes('hardcoded') || desc.includes('static')) {
    return 'Outdated data displayed to users - no real-time updates from database';
  }
  if (desc.includes('test data') || desc.includes('mock data')) {
    return 'Unprofessional appearance - users see fake data instead of real content';
  }
  if (desc.includes('todo') || desc.includes('fixme')) {
    return 'Incomplete feature - developer reminder indicates unfinished work';
  }
  
  return 'Mock data reduces user trust and product quality';
};

/**
 * Get priority label based on severity
 */
const getPriority = (violation: MockViolation): string => {
  switch (violation.severity) {
    case 'critical': return '🚨 Fix IMMEDIATELY (before any deployment)';
    case 'high': return '⚠️ Fix before production release';
    case 'medium': return '📝 Fix in next sprint';
    case 'low': return '💡 Nice to have';
    default: return 'Review and fix when possible';
  }
};

/**
 * Get severity emoji
 */
const getSeverityEmoji = (severity: string): string => {
  switch (severity) {
    case 'critical': return '🔴';
    case 'high': return '🟠';
    case 'medium': return '🟡';
    case 'low': return '🟢';
    default: return '⚪';
  }
};

/**
 * Format violation with AI-powered auto-fix suggestions
 */
const formatViolationWithAutoFix = (violation: MockViolation, index: number): string => {
  // Generate AI-powered fix suggestion
  const autoFix: AutoFixSuggestion = generateAutoFix(violation);
  
  let report = `\n### ${index}. ${autoFix.fixType === 'database_query' ? '🗄️ Database Integration Required' : '🔧 Code Refactor Needed'}: ${violation.description}\n\n`;
  
  // Severity + Business Impact
  report += `**Severity:** ${getSeverityEmoji(violation.severity)} ${violation.severity.toUpperCase()}\n`;
  
  // Confidence Score (NEW!)
  if (violation.confidence !== undefined) {
    const confLevel = getConfidenceLevel(violation.confidence);
    report += `**Confidence:** ${confLevel.emoji} ${violation.confidence}% ${confLevel.label}\n`;
    if (violation.reasoning) {
      report += `**Reasoning:** ${violation.reasoning}\n`;
    }
  }
  
  report += `**Business Impact:** ${getBusinessImpact(violation)}\n`;
  report += `**Estimated Fix Time:** ${autoFix.estimatedTime}\n`;
  report += `**Difficulty:** ${autoFix.difficulty.charAt(0).toUpperCase() + autoFix.difficulty.slice(1)}\n`;
  report += `**Priority:** ${getPriority(violation)}\n\n`;
  
  // Location (expanded with line range)
  const lineRange = violation.line ? `${violation.line}-${violation.line + 5}` : 'unknown';
  report += `**Location:** \`${violation.file}:${lineRange}\`\n\n`;
  
  // Code Context (expanded)
  if (violation.code) {
    report += `**Code Context:**\n\`\`\`typescript\n${violation.code}\n\`\`\`\n\n`;
  }
  
  // WHY THIS IS CRITICAL
  report += `**Why This Is Critical:**\n${autoFix.explanation}\n\n`;
  
  // CONCRETE FIX (copy-paste ready)
  report += `**Concrete Fix (Copy & Paste Ready):**\n\`\`\`typescript\n${autoFix.codeSnippet}\n\`\`\`\n\n`;
  
  // Files to modify
  if (autoFix.requiredFiles && autoFix.requiredFiles.length > 0) {
    report += `**Files to Modify:** ${autoFix.requiredFiles.join(', ')}\n`;
  }
  
  // Confidence
  report += `**AI Confidence:** ${autoFix.confidence}%\n\n`;
  
  report += `---\n`;
  return report;
};

/**
 * Generate "How to Read This Report" guide
 */
const generateReportGuide = (): string => {
  let guide = `## 📖 How to Read This Report\n\n`;
  guide += `**Confidence Scores:**\n`;
  guide += `- 🟢 85-100%: High confidence - Very likely real mock data\n`;
  guide += `- 🟡 60-84%: Medium confidence - Review context carefully\n`;
  guide += `- 🔴 Below 60%: Low confidence - Filtered out by default\n\n`;
  guide += `**Severity Levels:**\n`;
  guide += `- 🔴 Critical: Security risks or data exposure - Fix immediately\n`;
  guide += `- 🟠 High: Production-breaking issues - Fix before deployment\n`;
  guide += `- 🟡 Medium: Quality/UX issues - Fix in next sprint\n`;
  guide += `- 🟢 Low: Minor issues - Nice to have\n\n`;
  guide += `**Priority Sorting:**\n`;
  guide += `Issues are sorted by priority score (severity × confidence) for smart prioritization.\n\n`;
  guide += `---\n\n`;
  return guide;
};

/**
 * Generate executive summary with priority breakdown and confidence info
 */
const generateExecutiveSummary = (violations: MockViolation[]): string => {
  const critical = violations.filter(v => v.severity === 'critical').length;
  const high = violations.filter(v => v.severity === 'high').length;
  const medium = violations.filter(v => v.severity === 'medium').length;
  const low = violations.filter(v => v.severity === 'low').length;
  
  // Confidence breakdown
  const veryHighConf = violations.filter(v => (v.confidence || 50) >= 85).length;
  const highConf = violations.filter(v => (v.confidence || 50) >= 60 && (v.confidence || 50) < 85).length;
  const mediumConf = violations.filter(v => (v.confidence || 50) < 60).length;
  
  // Calculate total estimated fix time
  const autoFixes = violations.map(generateAutoFix);
  const totalMinutes = autoFixes.reduce((sum, fix) => {
    const mins = parseInt(fix.estimatedTime.match(/\d+/)?.[0] || '0');
    return sum + mins;
  }, 0);
  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  
  let summary = `## 📊 Executive Summary\n\n`;
  summary += `**Total Issues Found:** ${violations.length}\n`;
  summary += `**Estimated Total Fix Time:** ${totalHours > 0 ? `${totalHours}h ` : ''}${remainingMinutes}min\n`;
  summary += `**Recommended Action:** ${critical > 0 ? '🚨 URGENT: Fix critical issues immediately' : high > 0 ? '⚠️ Fix high-priority issues before deployment' : '✅ Address issues in next sprint'}\n\n`;
  
  summary += `### Severity Breakdown:\n`;
  if (critical > 0) summary += `- 🔴 **${critical} Critical** (Fix immediately - security/data risks)\n`;
  if (high > 0) summary += `- 🟠 **${high} High** (Fix before production deployment)\n`;
  if (medium > 0) summary += `- 🟡 **${medium} Medium** (Fix in next sprint)\n`;
  if (low > 0) summary += `- 🟢 **${low} Low** (Nice to have)\n`;
  summary += `\n`;
  
  // Confidence Breakdown (NEW!)
  summary += `### Confidence Breakdown:\n`;
  if (veryHighConf > 0) summary += `- 🟢 **${veryHighConf} High Confidence** (85-100%) - Definitely fix these\n`;
  if (highConf > 0) summary += `- 🟡 **${highConf} Medium Confidence** (60-84%) - Review carefully\n`;
  if (mediumConf > 0) summary += `- 🔴 **${mediumConf} Low Confidence** (below 60%) - May be false positives\n`;
  summary += `\n`;
  
  // Priority action items (top 3) - sorted by priority score
  const topPriority = violations
    .sort((a, b) => getPriorityScore(b) - getPriorityScore(a))
    .slice(0, 3);
  
  if (topPriority.length > 0) {
    summary += `### 🎯 Top Priority Fixes:\n`;
    topPriority.forEach((v, i) => {
      const fix = generateAutoFix(v);
      const fileShort = v.file?.split('/').pop() || 'unknown';
      summary += `${i + 1}. **${v.description}** (${fix.estimatedTime}, ${fileShort}:${v.line})\n`;
    });
    summary += `\n`;
  }
  
  summary += `---\n\n`;
  return summary;
};

export const generateLovableReport = (scanResult: ScanResult, compact: boolean = false): string => {
  const { mockDataViolations, totalViolations, scannedAt } = scanResult;

  let report = `# MockDetector Pro - Professional Scan Report\n\n`;
  report += `**Scanned:** ${scannedAt.toLocaleString()}\n`;
  report += `**Total Issues:** ${totalViolations}\n`;
  report += `**Scan Duration:** ${Math.round(scanResult.scanDuration)}ms\n\n`;

  if (totalViolations === 0) {
    report += `✅ **No issues found!** Your project is clean.\n`;
    return report;
  }

  // How to Read This Report (NEW!)
  report += generateReportGuide();
  
  // Executive Summary
  report += generateExecutiveSummary(mockDataViolations);
  
  // In compact mode, limit details
  const maxViolationsToShow = compact ? 10 : Infinity;

  // Sort violations by priority score (severity × confidence)
  const sortedViolations = [...mockDataViolations].sort((a, b) => 
    getPriorityScore(b) - getPriorityScore(a)
  );

  // Mock Data Violations with Auto-Fix
  if (sortedViolations.length > 0) {
    report += `## 🚨 Detailed Violations with Fix Suggestions\n\n`;
    
    const violationsToShow = sortedViolations.slice(0, maxViolationsToShow);
    violationsToShow.forEach((v, i) => {
      report += formatViolationWithAutoFix(v, i + 1);
    });
    
    if (sortedViolations.length > maxViolationsToShow) {
      const remaining = sortedViolations.length - maxViolationsToShow;
      report += `\n_...and ${remaining} more violations. Download full report to see all details._\n\n`;
    }
  }

  report += `---\n\n`;
  report += `**Generated by MockDetector Pro v${WIDGET_VERSION}**\n`;
  report += `_Professional-grade reports with AI-powered fix suggestions and confidence scoring_\n\n`;
  
  if (compact) {
    report += `💡 **Tip:** This is a compact view showing first 10 issues. Use "Download Report" for complete analysis.\n`;
  }

  return report;
};

// Generate a downloadable full report
export const generateFullReport = (scanResult: ScanResult): string => {
  return generateLovableReport(scanResult, false);
};

// Generate a compact report for chat display
export const generateCompactReport = (scanResult: ScanResult): string => {
  return generateLovableReport(scanResult, true);
};

// Download report as .txt file (easier to open and upload)
export const downloadReportAsTxt = (scanResult: ScanResult, filename: string = 'mockdetector-report.txt'): void => {
  const report = generateFullReport(scanResult);
  const blob = new Blob([report], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const copyReportToClipboard = async (scanResult: ScanResult): Promise<boolean> => {
  try {
    const report = generateCompactReport(scanResult);
    await navigator.clipboard.writeText(report);
    return true;
  } catch (error) {
    console.error('Failed to copy report:', error);
    return false;
  }
};

export const copyFullReportToClipboard = async (scanResult: ScanResult): Promise<boolean> => {
  try {
    const report = generateFullReport(scanResult);
    await navigator.clipboard.writeText(report);
    return true;
  } catch (error) {
    console.error('Failed to copy full report:', error);
    return false;
  }
};

// ============= COMBINED REPORT FUNCTIONS (Frontend + Database) =============

/**
 * Format database violation - COMPACT VERSION (no repetitive Business Impact)
 */
const formatDatabaseViolation = (violation: any, index: number): string => {
  const severityEmoji = violation.severity === 'critical' ? '🔴' : 
                       violation.severity === 'high' ? '🟠' : 
                       violation.severity === 'medium' ? '🟡' : '🟢';
  
  // One-liner format for database issues (most are false positives anyway)
  let report = `${index}. ${severityEmoji} **${violation.table}.${violation.column}**: ${violation.description} (${violation.affectedRows} rows, ${violation.confidence}% confidence)\n`;
  
  return report;
};

export const generateCombinedReport = (
  scanResult: ScanResult | null, 
  databaseResult: DatabaseScanResult | null,
  compact: boolean = false
): string => {
  const frontendViolations = scanResult?.totalViolations || 0;
  const databaseViolations = databaseResult?.totalViolations || 0;
  const totalIssues = frontendViolations + databaseViolations;

  let report = `# MockDetector Pro - Combined Professional Report\n\n`;
  
  // Summary header
  if (scanResult) {
    report += `**Frontend Scan:** ${scanResult.scannedAt.toLocaleString()}\n`;
    report += `**Frontend Issues:** ${frontendViolations}\n`;
  }
  if (databaseResult) {
    report += `**Database Scan:** ${databaseResult.scannedAt.toLocaleString()}\n`;
    report += `**Database Issues:** ${databaseViolations}\n`;
    report += `**Tables Scanned:** ${databaseResult.tablesScanned}\n`;
  }
  report += `**Total Issues:** ${totalIssues}\n\n`;

  if (totalIssues === 0) {
    report += `✅ **No issues found!** Your project is clean.\n`;
    return report;
  }

  // Combined Executive Summary (NEW!)
  const allViolations: MockViolation[] = [
    ...(scanResult?.mockDataViolations || []),
    ...(databaseResult?.violations.map(v => ({
      type: 'mock_data' as const,
      severity: v.severity,
      description: `Database: ${v.description}`,
      code: v.examples.join(', '),
      file: `${v.table}.${v.column}`,
      line: 0,
      fix: 'Clean database records'
    })) || [])
  ];
  
  // How to Read This Report
  report += generateReportGuide();
  
  report += generateExecutiveSummary(allViolations);
  
  const maxViolationsToShow = compact ? 10 : Infinity;

  // Frontend Issues with Auto-Fix
  if (scanResult && scanResult.mockDataViolations.length > 0) {
    report += `## 🚨 Frontend Mock Data Violations (${scanResult.mockDataViolations.length})\n\n`;
    
    // Sort by priority score (severity × confidence)
    const sortedFrontend = [...scanResult.mockDataViolations].sort((a, b) => 
      getPriorityScore(b) - getPriorityScore(a)
    );
    
    const violationsToShow = sortedFrontend.slice(0, maxViolationsToShow);
    violationsToShow.forEach((v, i) => {
      report += formatViolationWithAutoFix(v, i + 1);
    });
    
    if (sortedFrontend.length > maxViolationsToShow) {
      const remaining = sortedFrontend.length - maxViolationsToShow;
      report += `\n_...and ${remaining} more frontend violations._\n\n`;
    }
  }

  // Database Issues - COMPACT LIST (no verbose details)
  if (databaseResult && databaseResult.violations.length > 0) {
    report += `## 🗄️ Database Scan Results (${databaseResult.violations.length})\n\n`;
    report += `⚠️ **Database Scan Disclaimer:** Database violations may include false positives. Each violation includes a confidence score to help you identify likely issues. High confidence (≥85%) violations should be reviewed carefully, while lower confidence items may be legitimate data patterns.\n\n`;
    
    // Sort database violations by severity
    const sortedDatabase = [...databaseResult.violations].sort((a, b) => 
      getSeverityScore(b.severity) - getSeverityScore(a.severity)
    );
    
    const violationsToShow = compact ? sortedDatabase.slice(0, 5) : sortedDatabase.slice(0, 10);
    violationsToShow.forEach((v, i) => {
      report += formatDatabaseViolation(v, i + 1);
    });
    
    if (sortedDatabase.length > violationsToShow.length) {
      const remaining = sortedDatabase.length - violationsToShow.length;
      report += `\n_...and ${remaining} more database findings. Download full report for complete details._\n\n`;
    } else {
      report += `\n`;
    }
  }

  report += `---\n\n`;
  report += `**Generated by MockDetector Pro v${WIDGET_VERSION}**\n`;
  report += `_Professional-grade combined analysis with AI-powered fix suggestions and confidence scoring_\n\n`;
  
  if (compact) {
    report += `💡 **Tip:** This is a compact view showing first 10 issues. Download complete report for full analysis.\n`;
  }

  return report;
};

export const generateCombinedCompactReport = (
  scanResult: ScanResult | null,
  databaseResult: DatabaseScanResult | null
): string => {
  return generateCombinedReport(scanResult, databaseResult, true);
};

export const generateCombinedFullReport = (
  scanResult: ScanResult | null,
  databaseResult: DatabaseScanResult | null
): string => {
  return generateCombinedReport(scanResult, databaseResult, false);
};

export const copyCombinedReportToClipboard = async (
  scanResult: ScanResult | null,
  databaseResult: DatabaseScanResult | null,
  compact: boolean = true
): Promise<{ success: boolean; length: number }> => {
  try {
    const report = generateCombinedReport(scanResult, databaseResult, compact);
    const length = report.length;
    
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(report);
      return { success: true, length };
    }
    
    // Fallback: textarea copy method for browsers without clipboard API
    const textarea = document.createElement('textarea');
    textarea.value = report;
    textarea.style.cssText = 'position:fixed;left:-9999px;top:0;';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    
    return { success, length };
  } catch (error) {
    console.error('Failed to copy combined report:', error);
    return { success: false, length: 0 };
  }
};

export const downloadCombinedReportAsTxt = (
  scanResult: ScanResult | null,
  databaseResult: DatabaseScanResult | null,
  filename: string = 'mockdetector-combined-report.txt'
): void => {
  const report = generateCombinedFullReport(scanResult, databaseResult);
  const blob = new Blob([report], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
